// Переход в витрину единого профиля IEC ID (docs/INTEGRATION-GUIDE.md §9.4).
//
// ОДНО место на приложение: имя, аватар и согласия правятся ТОЛЬКО там, и
// поверхностей, которые туда отправляют, уже несколько (форма профиля, меню
// профиля, настройки приватности, экран подписки). Вторая копия адреса
// разъедется первой же сменой раздела.
//
// Переход идёт через ОДНОРАЗОВЫЙ handoff (`iecId.portal.open`), а не прямой
// ссылкой: по прямой человек приземляется в витрине ГОСТЕМ и входит второй раз
// — ровно то, ради чего затевался единый профиль. Код живёт 60 секунд, поэтому
// он запрашивается в момент клика и тут же используется, а не кладётся в
// разметку заранее.

import { toast } from "@studyhub/ui/toast";
import i18n from "@/i18n";
import { IS_CAPACITOR } from "@/lib/platform-detect";
import { trpcMutation } from "@/lib/trpc-fetch";

/**
 * Адрес единого профиля экосистемы. Совпадает с серверным `IEC_ID_API_URL`
 * (`apps/server/src/shared/config/load.ts`) — стенды экосистемы живут на других
 * хостах, поэтому значение переопределяется на этапе сборки.
 *
 * Живёт здесь, а не рядом с формой входа: адрес нужен и компонентам
 * (`components/auth/auth-flow-target.ts` — экран согласия), и слою без React
 * (`lib/iec-session-shortcut.ts` — короткий путь входа). Держать его в
 * `components/` значило бы, что `hooks/` и `lib/` тянут зависимость на фичу, —
 * граница, которую сторожит `pnpm lint:arch:web`.
 */
export const IEC_ID_AUTH_URL: string =
	import.meta.env.VITE_IEC_ID_AUTH_URL ?? "https://auth.iec.exchange";

/** Разделы витрины — подмножество `iecPortalSectionSchema` из контракта. */
export type IecIdPortalSection =
	| "home"
	| "personal"
	| "security"
	| "notifications"
	| "legal"
	| "points"
	| "plus"
	| "services";

/**
 * Адрес витрины. Совпадает с серверным дефолтом `IEC_ID_SHOWCASE_URL`
 * (`apps/server/src/shared/config/load.ts`). Переопределяется на этапе сборки —
 * стенды экосистемы живут на других хостах.
 *
 * Используется ТОЛЬКО как запасной путь: рабочий переход собирает сервер.
 */
export const IEC_ID_SHOWCASE_URL: string =
	import.meta.env.VITE_IEC_ID_SHOWCASE_URL ?? "https://id.iec.exchange";

/**
 * Разделы витрины, куда разрешено вести из мобильного приложения.
 *
 * Только управление аккаунтом: личные данные, вход, доступ сервисов к данным
 * («Сервисы» — там выдают data-scope), уведомления, юридические документы и
 * согласия. «Баллы» и IEC+ — то, что в экосистеме ПРОДАЁТСЯ; ссылка на них из
 * приложения — это и показ контента,
 * купленного вне приложения, и призыв купить мимо IAP (App Store 3.1.1 /
 * 3.1.3(f), отказ сборки StudyHub 1.1.1 (9) от 02.11.2026). Главная витрины
 * («home») ведёт туда же одной плиткой, поэтому тоже закрыта.
 *
 * Список — единственный источник правила: UI прячет пункты по нему же
 * (`canOpenIecIdSection`), а `openIecIdSection` отказывается открыть раздел вне
 * списка, даже если новая точка входа забудет спросить.
 */
const APP_ALLOWED_SECTIONS: ReadonlySet<IecIdPortalSection> = new Set([
	"personal",
	"security",
	"services",
	"notifications",
	"legal",
]);

/** Можно ли открыть раздел витрины с текущей поверхности. */
export function canOpenIecIdSection(section: IecIdPortalSection): boolean {
	return !IS_CAPACITOR || APP_ALLOWED_SECTIONS.has(section);
}

/** Прямой адрес раздела — запасной путь, когда handoff не выдали. */
function directUrl(section: IecIdPortalSection): string {
	return section === "home" ? IEC_ID_SHOWCASE_URL : `${IEC_ID_SHOWCASE_URL}/${section}`;
}

/**
 * Открыть раздел витрины.
 *
 * Web — новая вкладка (человек не теряет своё занятие), Capacitor — тоже
 * `_blank`: WebView отдаёт такую ссылку системному браузеру, а навигация
 * текущего окна увела бы приложение с бандла без пути назад.
 *
 * Возвращает `void` намеренно: вызывающие — обработчики клика, и ни один из них
 * не ждёт результата. Ошибка не проглатывается — она становится и тостом, и
 * запасным переходом по прямой ссылке (гостем, но кликнувший всё же доедет),
 * потому что молча неработающий пункт меню хуже честной деградации.
 */
export function openIecIdSection(section: IecIdPortalSection = "home"): void {
	// Страховка на случай точки входа, которая не спросила `canOpenIecIdSection`.
	// Тихо: в приложении такой пункт не должен был появиться вовсе.
	if (!canOpenIecIdSection(section)) return;
	// Вкладку открываем СИНХРОННО, до сетевого запроса: браузер разрешает
	// `window.open` только внутри жеста пользователя, а после `await` жест уже
	// потерян и попап блокируется. В Capacitor блокировщика нет — там ссылку
	// отдаём системному браузеру уже с готовым адресом.
	//
	// `noopener` тут НЕ передаём: с ним `window.open` возвращает `null`, и
	// переставить адрес открытой вкладке будет нечем. Связь рвём вручную —
	// `opener = null` даёт тот же эффект.
	const pending = IS_CAPACITOR ? null : window.open("about:blank", "_blank");
	if (pending) pending.opener = null;

	void (async () => {
		try {
			const { url } = await trpcMutation<{ url: string }>("iecId.portal.open", { section });
			navigate(pending, url);
		} catch {
			// Деградация громкая: человек видит, что переход не собрался, и всё
			// равно попадает в витрину — пусть и гостем.
			toast.error(i18n.t("billing:iec.portalFailed"));
			navigate(pending, directUrl(section));
		}
	})();
}

function navigate(pending: Window | null, url: string): void {
	if (pending && !pending.closed) {
		pending.location.replace(url);
		return;
	}
	// Вкладку заблокировали (или мы в Capacitor) — открываем обычным путём.
	window.open(url, "_blank", IS_CAPACITOR ? "" : "noopener,noreferrer");
}
